import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
  Input,
} from "@elaniintc/goes-design-system";
import { maskitoPhoneOptionsGenerator } from "@maskito/phone";
import { useMaskito } from "@maskito/react";
import { type CountryCode } from "libphonenumber-js";
import { useEffect, useMemo, useRef, useState } from "react";
import metadata from "libphonenumber-js/max/metadata";
import { cn } from "@/utils/theme";
import { useGetCountryData } from "@/queries/me";

interface Country {
  country_code: string;
  name: string;
  calling_code: string;
  flag_url: string;
}

export interface InternationalPhoneValue {
  number: string;
  calling_code: string;
  country: string;
}

type InternationalPhoneInput = InternationalPhoneValue | string | null;

interface InternationalInputPhoneProps {
  value?: InternationalPhoneInput;
  onChange?: (value: InternationalPhoneValue) => void;
  onBlur?: () => void;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  error?: string;
}

const COUNTRY_FALLBACK: Country = {
  country_code: "SV",
  name: "El Salvador",
  calling_code: "503",
  flag_url: "https://flagcdn.com/w20/sv.webp",
};

const normalizeText = (text: string) => {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const getPhoneNumberValue = (value?: InternationalPhoneInput) => {
  if (!value) return "";

  if (typeof value === "string") return value;

  return value.number || "";
};

const getCountryFromValue = (value?: InternationalPhoneInput) => {
  if (!value || typeof value === "string") return null;

  return value.country || null;
};

export const InternationalInputPhone = ({
  value,
  onChange,
  onBlur,
  name,
  disabled = false,
  required = false,
  inputRef,
  error,
}: InternationalInputPhoneProps) => {
  const { data: countriesResponse, isLoading } = useGetCountryData();

  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState<Country[]>([COUNTRY_FALLBACK]);
  const [countryPage, setCountryPage] = useState(1);
  const [countrySelected, setCountrySelected] =
    useState<Country>(COUNTRY_FALLBACK);

  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const countryList = countriesResponse?.countries ?? [];

    if (!Array.isArray(countryList) || countryList.length === 0) return;

    setCountries(countryList);

    setCountrySelected((prev) => {
      const currentCountryExists = countryList.some(
        (country) => country.country_code === prev.country_code,
      );

      if (currentCountryExists) return prev;

      return countryList[0];
    });
  }, [countriesResponse]);

  useEffect(() => {
    const savedCountryCode = getCountryFromValue(value);

    if (!savedCountryCode || countries.length === 0) return;

    const match = countries.find(
      (country) => country.country_code === savedCountryCode,
    );

    if (match) {
      setCountrySelected(match);
    }
  }, [value, countries]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 0);
    }
  }, [search, open]);

  const phoneMask = useMaskito({
    options: useMemo(() => {
      return maskitoPhoneOptionsGenerator({
        countryIsoCode: countrySelected.country_code as CountryCode,
        metadata,
        strict: true,
        format: "NATIONAL",
      });
    }, [countrySelected.country_code]),
  });

  const phoneNumber = getPhoneNumberValue(value);

  const emitChange = (number: string, country = countrySelected) => {
    onChange?.({
      number,
      calling_code: country.calling_code,
      country: country.country_code,
    });
  };

  const handleSelectCountry = (country: Country) => {
    setCountrySelected(country);
    setOpen(false);
    setSearch("");
    setCountryPage(1);
    emitChange("", country);
  };

  const renderableCountries = useMemo(() => {
    const chunkSize = 20;
    const totalToShow = countryPage * chunkSize;
    const normalizedSearch = normalizeText(search);

    return countries
      .filter((country) => {
        const normalizedName = normalizeText(country.name);
        const normalizedCode = normalizeText(country.country_code);

        return (
          normalizedName.includes(normalizedSearch) ||
          country.calling_code.includes(search) ||
          normalizedCode.includes(normalizedSearch)
        );
      })
      .slice(0, totalToShow);
  }, [countries, countryPage, search]);

  const countriesWithoutSelected = useMemo(() => {
    return renderableCountries.filter(
      (country) => country.country_code !== countrySelected.country_code,
    );
  }, [renderableCountries, countrySelected.country_code]);

  const renderCountryItem = (country: Country, selected = false) => {
    return (
      <DropdownMenuItem
        key={country.country_code}
        onSelect={() => handleSelectCountry(country)}
        className={cn(
          "px-3 py-2 rounded-lg cursor-pointer",
          selected && "bg-surface-action-accent",
        )}
      >
        <div className="flex justify-between items-center w-full gap-2">
          <div className="flex items-center min-w-0">
            <img
              src={country.flag_url}
              alt={country.name}
              className="w-5 h-3 shrink-0"
            />

            <span
              className={cn(
                "pl-2 font-semibold text-body truncate",
                selected && "text-action",
              )}
            >
              {country.name}
            </span>
          </div>

          <span
            className={cn("pl-2 text-body shrink-0", selected && "text-action")}
          >
            (+{country.calling_code})
          </span>
        </div>
      </DropdownMenuItem>
    );
  };

  const setInputRefs = (element: HTMLInputElement | null) => {
    phoneMask(element);

    if (!inputRef) return;

    if (typeof inputRef === "function") {
      inputRef(element);
      return;
    }

    inputRef.current = element;
  };

  return (
    <GroupTextField
      className="w-full"
      label="Número de teléfono"
      id={name}
      name={name}
      error={error}
      required
    >
      <GroupTextFieldRow className="overflow-x-hidden">
        <DropdownMenu
          open={open}
          onOpenChange={(isOpen) => {
            if (disabled) {
              setOpen(false);
              return;
            }

            setOpen(isOpen);

            if (!isOpen) {
              setSearch("");
              setCountryPage(1);
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button className="h-full" group="left" variant="quiet">
              <span className="flex items-center gap-2">
                <img
                  src={countrySelected.flag_url}
                  alt={countrySelected.name}
                  className="w-5 h-3"
                />

                <span className="font-semibold">
                  {countrySelected.country_code}
                </span>
              </span>

              <Icon name="angle-down" type="far" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            side="bottom"
            align="start"
            className="z-9999 w-[320px] border-none p-0"
          >
            <div className="border-b border-neutral-quiet p-3 relative">
              <Icon
                type="far"
                name="search"
                className="absolute left-5 top-1/2 transform -translate-y-1/2 text-body"
              />

              <Input
                ref={searchRef}
                className="text-sm w-full font-normal flex-auto pl-9"
                placeholder="Buscar..."
                onChange={(e) => {
                  setCountryPage(1);
                  setSearch(e.target.value);
                }}
                value={search}
              />
            </div>

            <div className="max-h-48 p-2 overflow-y-auto">
              {isLoading ? (
                <div className="p-3 text-center text-body-quiet text-sm">
                  Cargando países...
                </div>
              ) : (
                <>
                  {countrySelected && (
                    <div className="mb-2">
                      {renderCountryItem(countrySelected, true)}
                    </div>
                  )}

                  <div className="border-b border-neutral-quiet mb-2" />

                  {countriesWithoutSelected.map((country) =>
                    renderCountryItem(country),
                  )}

                  {countries.length > renderableCountries.length &&
                    search.length === 0 && (
                      <Button
                        type="button"
                        variant="ghost-blue"
                        size="sm"
                        className="w-full mt-2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCountryPage((prev) => prev + 1);
                        }}
                      >
                        Cargar más
                      </Button>
                    )}

                  {renderableCountries.length === 0 && (
                    <div className="p-3 text-center text-body-quiet">
                      No se encontraron países
                    </div>
                  )}
                </>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="relative w-full flex items-center text-body">
          <div
            aria-label="Código de país"
            className="absolute left-2 top-1/2 -translate-y-1/2"
          >
            <Icon type="far" name="phone" size="sm" />

            <span className="pl-2 text-body-quiet text-sm">
              (+{countrySelected.calling_code})
            </span>
          </div>

          <Input
            id={name}
            name={name}
            ref={setInputRefs}
            value={phoneNumber}
            disabled={disabled}
            required={required}
            className={cn(
              "pl-19.5 w-full bg-neutral",
              countrySelected.calling_code.length === 1 && "pl-14.5",
              countrySelected.calling_code.length === 2 && "pl-17.5",
            )}
            placeholder={
              countrySelected.country_code === "SV" ? "0000 0000" : ""
            }
            group="right"
            onBlur={onBlur}
            onChange={(e) => {
              emitChange(e.target.value);
            }}
          />
        </div>
      </GroupTextFieldRow>
    </GroupTextField>
  );
};
